import { displayDateUS } from "../../utils/dateUtils";
import { getRnWelcomeLetterItems } from "../../components/RN/RnWelcomeLetter";
import { getLvnWelcomeLetterItems } from "../../components/Lvn/LvnWelcomeLetter";
import { getHhaWelcomeLetterItems } from "../../components/HHA/HhaWelcomeLetter";
import { getPtaWelcomeLetterItems } from "../../components/PTA/PtaWelcomLetter";
import { getPtWelcomeLetterItems } from "../../components/PT/PtWelcomeLetter";
import { getMswWelcomeLetterItems } from "../../components/MSW/MswWelcomeLetter";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

/**
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseWelcomeLetterFromApiResponse(response, targetUserId) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.welcomeLetter) {
    return result.welcomeLetter;
  }
  if (result?.data && typeof result.data === "object") {
    return result.data;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null || result.role != null || result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

/** Contractor agreement roles (Group 2 style signatures / payloads). */
const CONTRACTOR_ROLES = new Set(["PT", "OT", "ST", "MSW"]);

/**
 * @typedef {{ getter: (companyName: string) => unknown[], splitIndex: number }} WelcomeLetterPrintRoleConfig
 */

/** @type {Record<string, WelcomeLetterPrintRoleConfig>} */
const ROLE_PRINT_CONFIG = {
  RN: { getter: getRnWelcomeLetterItems, splitIndex: 15 },
  LVN: { getter: getLvnWelcomeLetterItems, splitIndex: 15 },
  HHA: { getter: getHhaWelcomeLetterItems, splitIndex: 10 },
  PTA: { getter: getPtaWelcomeLetterItems, splitIndex: 8 },
  OTA: { getter: getPtaWelcomeLetterItems, splitIndex: 8 },
  PT: { getter: getPtWelcomeLetterItems, splitIndex: 7 },
  OT: { getter: getPtWelcomeLetterItems, splitIndex: 7 },
  ST: { getter: getPtWelcomeLetterItems, splitIndex: 7 },
  MSW: { getter: getMswWelcomeLetterItems, splitIndex: 7 },
};

function normalizeWelcomeLetterRole(role) {
  const r = String(role ?? "")
    .trim()
    .toUpperCase();
  if (ROLE_PRINT_CONFIG[r]) return r;
  return "RN";
}

/**
 * @param {Record<string, unknown>} wl
 */
export function mergeWelcomeLetterData(wl) {
  const copy = { ...wl };
  const payRate =
    wl.payRate && typeof wl.payRate === "object" ? wl.payRate : {};
  Object.keys(payRate).forEach((k) => {
    copy[`payRate.${k}`] = /** @type {Record<string, unknown>} */ (payRate)[k];
  });

  const empSignedBy = wl.empSignedBy;
  let fromEmp =
    empSignedBy &&
    typeof empSignedBy === "object" &&
    (/** @type {{ firstName?: string; lastName?: string }} */ (empSignedBy)
      .firstName ||
      /** @type {{ firstName?: string; lastName?: string }} */ (empSignedBy)
        .lastName)
      ? `${/** @type {{ firstName?: string }} */ (empSignedBy).firstName || ""} ${/** @type {{ lastName?: string }} */ (empSignedBy).lastName || ""}`.trim()
      : "";

  const es = wl.employeeSignature;
  let fromEs =
    es && typeof es === "object"
      ? String(
          /** @type {{ name?: string; employee_name?: string }} */ (es).name ||
            /** @type {{ name?: string; employee_name?: string }} */ (es)
              .employee_name ||
            "",
        ).trim()
      : "";

  const displayName =
    (typeof wl.name === "string" && wl.name) ||
    (typeof wl.applicantName === "string" && wl.applicantName) ||
    fromEmp ||
    fromEs ||
    "";

  copy.employeeName = copy.employeeName || displayName;
  copy.contractorName = copy.contractorName || displayName;
  copy.applicantName = copy.applicantName || displayName;
  copy.supervisorName =
    copy.supervisorName ||
    (typeof wl.supervisor === "string" ? wl.supervisor : "") ||
    "";

  return copy;
}

/**
 * @param {Record<string, unknown>} data
 * @param {string} name
 */
function resolveField(data, name) {
  if (!name) return "";
  const direct = data[name];
  if (direct !== undefined && direct !== null && direct !== "") {
    return direct;
  }
  const parts = name.split(".");
  if (parts.length === 2 && data.payRate && typeof data.payRate === "object") {
    const v = /** @type {Record<string, unknown>} */ (data.payRate)[parts[1]];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return "";
}

function formatMoney(val) {
  if (val === undefined || val === null || val === "") return "—";
  const n = parseFloat(String(val).replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(n)) return escapeHtml(String(val));
  return `$${n.toFixed(2)}`;
}

function isDateFieldName(name, inputType) {
  if (inputType === "date") return true;
  const lower = String(name).toLowerCase();
  return (
    lower.includes("date") ||
    lower === "hiredate" ||
    lower === "effectivedate" ||
    lower === "firstpatientcaredate"
  );
}

/**
 * @param {unknown} item
 * @param {unknown} val
 */
function formatFieldDisplay(item, val) {
  const rec = /** @type {{ inputType?: string; name?: string }} */ (item);
  if (val === undefined || val === null || val === "") return "—";
  if (isDateFieldName(String(rec.name || ""), rec.inputType)) {
    return escapeHtml(formatDate(val));
  }
  return escapeHtml(String(val));
}

function renderContentAfter(contentAfter, data) {
  if (!Array.isArray(contentAfter)) return "";
  return contentAfter
    .map((content, idx) => {
      if (!content || typeof content !== "object") return "";
      const mode = /** @type {{ mode?: string }} */ (content).mode;
      if (mode === "p") {
        const text = /** @type {{ text?: string }} */ (content).text || "";
        return `<p class="wl-prose">${escapeHtml(text)}</p>`;
      }
      if (mode === "subheading") {
        const text = /** @type {{ text?: string }} */ (content).text || "";
        return `<h5 class="wl-sub">${escapeHtml(text)}</h5>`;
      }
      if (mode === "list") {
        const items = /** @type {{ items?: string[] }} */ (content).items || [];
        const lis = items
          .map((li) => `<li class="wl-li">${escapeHtml(li)}</li>`)
          .join("");
        return `<ol class="wl-ol">${lis}</ol>`;
      }
      if (mode === "steps") {
        const items =
          /** @type {{ items?: { step?: string; text?: string }[] }} */ (
            content
          ).items || [];
        return items
          .map((step, i) => {
            const st = step?.step || "";
            const tx = step?.text || "";
            return `<div class="wl-step"><span class="wl-step-num">${escapeHtml(st)}</span><span>${escapeHtml(tx)}</span></div>`;
          })
          .join("");
      }
      return "";
    })
    .join("");
}

/**
 * @param {unknown} clause
 * @param {Record<string, unknown>} data
 */
function clauseToHtml(clause, data) {
  if (!clause || typeof clause !== "object") return "";
  const c = /** @type {{
    number?: string;
    text?: string;
    inputName?: string;
    subClauses?: unknown[];
    addressFields?: unknown[];
    textAfter?: string;
  }} */ (clause);
  let inner = `<p class="wl-clause-text">${escapeHtml(c.text || "")}</p>`;
  if (c.inputName) {
    const raw = resolveField(data, c.inputName);
    inner += `<div class="wl-pay-inline"><strong>${formatMoney(raw)}</strong></div>`;
  }
  if (Array.isArray(c.subClauses) && c.subClauses.length > 0) {
    inner += `<div class="wl-subclauses">${c.subClauses.map((sc) => clauseToHtml(sc, data)).join("")}</div>`;
  }
  if (c.textAfter) {
    inner += `<p class="wl-prose">${escapeHtml(c.textAfter)}</p>`;
  }
  return `<div class="wl-clause"><span class="wl-clause-no">${escapeHtml(c.number || "")}</span><div class="wl-clause-body">${inner}</div></div>`;
}

/**
 * @param {unknown} item
 * @param {Record<string, unknown>} data
 */
function welcomeItemToHtml(item, data) {
  if (!item || typeof item !== "object") return "";
  const type = /** @type {{ type?: string }} */ (item).type;
  switch (type) {
    case "intro": {
      const it = /** @type {{
        company?: string;
        title?: string;
        text?: string;
        salutation?: string;
        salutationSuffix?: string;
        fields?: { name?: string; placeholder?: string; inputType?: string }[];
        textAfter?: string;
      }} */ (item);
      let fieldsHtml = "";
      if (Array.isArray(it.fields)) {
        fieldsHtml = it.fields
          .map((f) => {
            const v = resolveField(data, f.name || "");
            const display = formatFieldDisplay(f, v);
            return `<span class="wl-field-line">${display}</span>`;
          })
          .join(" ");
      }
      return `<div class="wl-intro">
        <div class="wl-company-title">${escapeHtml(it.company || "")}</div>
        <div class="wl-doc-title">${escapeHtml(it.title || "")}</div>
        <div class="wl-salutation">
          ${escapeHtml(it.salutation || "")}
          ${fieldsHtml}
          ${escapeHtml(it.salutationSuffix || "")}
        </div>
        ${it.text ? `<p class="wl-prose">${escapeHtml(it.text)}</p>` : ""}
        ${it.textAfter ? `<p class="wl-prose">${escapeHtml(it.textAfter)}</p>` : ""}
      </div>`;
    }
    case "field": {
      const it = /** @type {{
        label?: string;
        name?: string;
        inputType?: string;
      }} */ (item);
      const v = resolveField(data, it.name || "");
      const display = formatFieldDisplay(it, v);
      return `<div class="wl-field-row">
        <div class="wl-field-label">${escapeHtml(it.label || "")}</div>
        <div class="wl-field-value">${display}</div>
      </div>`;
    }
    case "text_header":
      return `<h4 class="wl-text-h">${escapeHtml(/** @type {{ text?: string }} */ (item).text || "")}</h4>`;
    case "section_header":
      return `<h3 class="wl-section-h">${escapeHtml(/** @type {{ title?: string }} */ (item).title || "")}</h3>`;
    case "pay_rate": {
      const it = /** @type {{ label?: string; name?: string; unit?: string }} */ (item);
      const raw = resolveField(data, it.name || "");
      return `<div class="wl-pay-row">
        <span class="wl-pay-label">${escapeHtml(it.label || "")}</span>
        <span class="wl-pay-amt">${formatMoney(raw)}</span>
        <span class="wl-pay-unit">${escapeHtml(it.unit || "")}</span>
      </div>`;
    }
    case "text_block":
      return `<p class="wl-prose">${escapeHtml(/** @type {{ text?: string }} */ (item).text || "")}</p>`;
    case "section": {
      const it = /** @type {
        title?: string;
        text?: string;
        bullets?: string[];
        definitions?: { term?: string; def?: string }[];
        content_after?: unknown[];
      } */ (item);
      let bulletsHtml = "";
      if (Array.isArray(it.bullets)) {
        bulletsHtml = `<ul class="wl-ul">${it.bullets.map((b) => `<li>${escapeHtml(b)}</li>`).join("")}</ul>`;
      }
      let defHtml = "";
      if (Array.isArray(it.definitions)) {
        defHtml = it.definitions
          .map(
            (d) =>
              `<div class="wl-def"><strong>${escapeHtml(d.term || "")}:</strong> <span>${escapeHtml(d.def || "")}</span></div>`,
          )
          .join("");
      }
      const after = renderContentAfter(it.content_after || [], data);
      return `<div class="wl-section">
        <h4 class="wl-section-title">${escapeHtml(it.title || "")}</h4>
        ${it.text ? `<p class="wl-prose">${escapeHtml(it.text)}</p>` : ""}
        ${bulletsHtml}
        ${defHtml}
        ${after}
      </div>`;
    }
    case "part": {
      const it = /** @type {{
        partNumber?: string | number;
        title?: string;
        clauses?: unknown[];
      }} */ (item);
      const clauses = Array.isArray(it.clauses)
        ? it.clauses.map((cl) => clauseToHtml(cl, data)).join("")
        : "";
      return `<div class="wl-part">
        <h4 class="wl-part-head">PART ${escapeHtml(String(it.partNumber ?? ""))}: ${escapeHtml(it.title || "")}</h4>
        <div class="wl-part-body">${clauses}</div>
      </div>`;
    }
    default:
      return "";
  }
}

function signatureImgOrText(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:image") || s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

/**
 * @param {Record<string, unknown>} wl
 * @param {string} normalizedRole
 */
function buildSignatureHtml(wl, normalizedRole) {
  const contractor = CONTRACTOR_ROLES.has(normalizedRole);
  if (contractor) {
    const es = wl.employeeSignature;
    const ad = wl.agencyDetails;
    const empName =
      (es &&
        typeof es === "object" &&
        (/** @type {{ name?: string; employee_name?: string }} */ (es).name ||
          /** @type {{ name?: string; employee_name?: string }} */ (es)
            .employee_name)) ||
      wl.contractorName ||
      wl.name ||
      "";
    const empSig =
      es && typeof es === "object"
        ? /** @type {{ signature?: string }} */ (es).signature
        : wl.applicantSignature || wl.empSignature;
    const adminName =
      ad && typeof ad === "object"
        ? String(
            /** @type {{ name?: string; agency_name?: string }} */ (ad).name ||
              "",
          )
        : String(wl.reviewedBySignedBy || "");
    const adminSig =
      ad && typeof ad === "object"
        ? /** @type {{ signature?: string }} */ (ad).signature
        : wl.reviewedBySignature;

    return `<div class="wl-signatures two-col">
      <div class="sig-card">
        <h3>Contractor / employee</h3>
        <div class="sig-label">Name</div>
        <div class="sig-line">${escapeHtml(String(empName || "—"))}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureImgOrText(empSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${escapeHtml(formatDate(wl.applicantSignedAt || wl.empSignedAt))}</div>
      </div>
      <div class="sig-card">
        <h3>Agency</h3>
        <div class="sig-label">Name / title</div>
        <div class="sig-line">${escapeHtml(adminName || "—")}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureImgOrText(adminSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${escapeHtml(formatDate(wl.reviewedBySignedAt))}</div>
      </div>
    </div>`;
  }

  const empSig = wl.empSignature || wl.applicantSignature;
  return `<div class="wl-signatures two-col">
    <div class="sig-card">
      <h3>Employee</h3>
      <div class="sig-label">Name</div>
      <div class="sig-line">${escapeHtml(String(wl.applicantName || wl.name || "—"))}</div>
      <div class="sig-label">Signature</div>
      <div class="sig-line">${signatureImgOrText(empSig)}</div>
      <div class="sig-label">Date</div>
      <div class="sig-line">${escapeHtml(formatDate(wl.empSignedAt || wl.applicantSignedAt))}</div>
    </div>
    <div class="sig-card">
      <h3>Reviewer</h3>
      <div class="sig-label">Name</div>
      <div class="sig-line">${escapeHtml(String(wl.reviewedBySignedBy || "—"))}</div>
      <div class="sig-label">Signature</div>
      <div class="sig-line">${signatureImgOrText(wl.reviewedBySignature)}</div>
      <div class="sig-label">Date</div>
      <div class="sig-line">${escapeHtml(formatDate(wl.reviewedBySignedAt))}</div>
    </div>
  </div>`;
}

function printHeader(companyName, logoUrl, subtitle) {
  const co = escapeHtml(companyName || "Company");
  const logo = escapeHtml(logoUrl || "");
  const sub = escapeHtml(subtitle || "");
  return `<div class="top">
    <img class="logo" src="${logo}" alt="" onerror="this.style.display='none'" />
    <div class="title-block">
      <p class="title-main">Welcome Letter &amp; Agreement</p>
      <p class="title-sub">${sub}</p>
      <p class="title-co">${co}</p>
    </div>
  </div>`;
}

/**
 * Print-ready HTML for completed Welcome Letter records (all roles).
 * @param {Record<string, unknown>} wl API welcome letter
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildWelcomeLetterPrintHtml(wl, branding) {
  const companyName = branding.companyName || "Company";
  const logoUrl = branding.logoUrl || "";
  const merged = mergeWelcomeLetterData(wl);
  const normalizedRole = normalizeWelcomeLetterRole(wl.role);
  const config =
    ROLE_PRINT_CONFIG[normalizedRole] || ROLE_PRINT_CONFIG.RN;
  const items = config.getter(companyName);
  const splitIndex = config.splitIndex;

  const page1 = items
    .slice(0, splitIndex)
    .map((it) => welcomeItemToHtml(it, merged))
    .join("");
  const page2 = items
    .slice(splitIndex)
    .map((it) => welcomeItemToHtml(it, merged))
    .join("");

  const ack =
    merged.applicantStatementAccepted !== undefined &&
    merged.applicantStatementAccepted !== null
      ? `<div class="cert-box">
    <div class="cert-row">
      <span class="chk">${merged.applicantStatementAccepted ? "☑" : "☐"}</span>
      <span>I acknowledge that I have reviewed and agree to the statements and policies in this document.</span>
    </div>
  </div>`
      : "";

  const sigHtml = buildSignatureHtml(merged, normalizedRole);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Welcome Letter — ${escapeHtml(String(merged.applicantName || merged.name || "Employee"))}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 11px;
      line-height: 1.45;
      color: #111827;
      margin: 0;
      padding: 12px;
    }
    .sheet { max-width: 880px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body {
        padding: 28px 18px 40px;
        background: transparent;
        min-height: 100vh;
      }
      .sheet {
        background: #ffffff;
        padding: 32px 36px 40px;
        border: 1px solid #e2e8f0;
        border-radius: 1px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.05),
          0 24px 48px -16px rgba(15, 23, 42, 0.22);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet {
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
      }
    }
    .wl-page-break { page-break-after: always; }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 16px;
    }
    .logo { height: 56px; width: auto; max-width: 200px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 16px; font-weight: 700; color: #111827; margin: 0 0 4px 0; }
    .title-sub { font-size: 13px; font-weight: 500; color: #2563eb; margin: 0 0 4px 0; }
    .title-co { font-size: 11px; color: #6b7280; margin: 0; }
    .wl-intro { text-align: center; margin-bottom: 20px; }
    .wl-company-title { font-size: 15px; font-weight: 700; color: #1e3a5f; margin-bottom: 8px; }
    .wl-doc-title { font-size: 13px; font-weight: 700; margin-bottom: 12px; text-transform: uppercase; }
    .wl-salutation { font-size: 12px; margin-bottom: 12px; }
    .wl-field-line {
      display: inline-block;
      border-bottom: 1px solid #374151;
      min-width: 180px;
      margin: 0 6px;
      padding: 2px 6px;
    }
    .wl-prose { text-align: justify; margin: 0 0 10px 0; font-size: 11px; color: #374151; }
    .wl-field-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
      margin-bottom: 10px;
      align-items: start;
    }
    .wl-field-label { font-weight: 600; font-size: 10px; color: #6b7280; }
    .wl-field-value {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 8px 10px;
      min-height: 34px;
      font-size: 11px;
    }
    .wl-text-h { font-weight: 700; margin: 16px 0 8px 0; font-size: 12px; }
    .wl-section-h { font-size: 14px; font-weight: 700; text-align: center; margin: 20px 0 12px 0; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
    .wl-pay-row {
      display: grid;
      grid-template-columns: 1fr auto 1.2fr;
      gap: 8px;
      padding: 4px 0;
      font-size: 11px;
      border-bottom: 1px solid #f3f4f6;
    }
    .wl-pay-label { font-weight: 600; }
    .wl-pay-amt { font-weight: 700; color: #0f172a; }
    .wl-pay-unit { color: #6b7280; font-size: 10px; }
    .wl-section { margin-bottom: 16px; page-break-inside: auto; }
    .wl-section-title {
      font-size: 12px;
      font-weight: 700;
      margin: 0 0 8px 0;
      color: #111827;
    }
    .wl-ul, .wl-ol { margin: 8px 0; padding-left: 20px; }
    .wl-li { margin-bottom: 4px; font-size: 10px; text-align: justify; color: #374151; }
    .wl-def { margin: 8px 0; padding-left: 10px; border-left: 3px solid #bfdbfe; font-size: 10px; text-align: justify; color: #374151; }
    .wl-part { margin-bottom: 18px; }
    .wl-part-head {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      margin: 0 0 10px 0;
      padding-bottom: 6px;
      border-bottom: 1px solid #e5e7eb;
    }
    .wl-clause { display: flex; gap: 10px; margin-bottom: 10px; font-size: 10px; text-align: justify; color: #374151; }
    .wl-clause-no { font-weight: 700; flex-shrink: 0; min-width: 36px; }
    .wl-clause-body { flex: 1; }
    .wl-clause-text { margin: 0 0 6px 0; }
    .wl-subclauses { margin-left: 12px; margin-top: 8px; }
    .wl-pay-inline { margin-top: 6px; font-size: 11px; }
    .wl-muted { color: #9ca3af; font-size: 9px; margin-left: 6px; }
    .wl-step { display: flex; gap: 8px; margin-bottom: 6px; font-size: 10px; }
    .wl-step-num { font-weight: 700; flex-shrink: 0; }
    .wl-sub { font-size: 11px; font-weight: 600; margin: 10px 0 6px 0; }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-top: 20px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fafafa;
    }
    .sig-card h3 { font-size: 12px; font-weight: 700; margin: 0 0 10px 0; color: #111827; }
    .sig-label {
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.06em;
      color: #6b7280;
      margin: 8px 0 4px 0;
    }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 28px;
      font-size: 12px;
      padding: 4px 0;
    }
    .sig-img { max-height: 48px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .cert-box {
      margin-top: 16px;
      padding: 12px 14px;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
    }
    .cert-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      font-size: 11px;
    }
    .chk { font-size: 14px; line-height: 1.2; }
    .footer-print {
      margin-top: 20px;
      padding-top: 10px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 10px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet wl-page-break">
    ${printHeader(companyName, logoUrl, `Role: ${normalizedRole}`)}
    ${page1}
    <div class="footer-print">Page 1</div>
  </div>
  <div class="sheet">
    ${printHeader(companyName, logoUrl, `Role: ${normalizedRole} (continued)`)}
    ${page2}
    ${ack}
    ${sigHtml}
    <div class="footer-print">— End of document —</div>
  </div>
</body>
</html>`;
}
